import React from "react";
import {
  Tag,
  Button,
  Avatar,
  DatePicker,
  Space,
  Modal,
  message,
  Select,
  Input,
} from "antd";
import { transaction as transactionApi, property as propertyApi } from "../../apis";
import {
  PageContainer,
  PageHeader,
  PageContent,
} from "../../components/Layout";
import { MyTable, RowAction } from "../../components";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Filter } from "../../components/Design";
import usePermission from "../../hooks/usePermission";
import moment from "moment";
import styled from "styled-components";
import { dateFormat, datetimeFormat, renderUser, yen } from "../../utils";
import { ExclamationCircleOutlined, UserOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslate } from "../../hooks";
import useFetch from "../../hooks/useFetch";
import { Flex } from "@mantine/core";

export default () => {
  const { translate } = useTranslate();
  const history = useHistory();
  const myTableRef = React.useRef();
  const params = useParams();
  const { search } = useLocation();
  const didInit = React.useRef(false);
  const debounceRef = React.useRef();
  const paramsSearch = React.useMemo(
    () => new URLSearchParams(search),
    [search]
  );
  const { s3host, roles, paymentStatus } = useSelector(
    (state) => state.general
  );
  const { checkPermission } = usePermission();
  const [properties] = useFetch(propertyApi.selectAdmin)([]);
  const savedFilters = JSON.parse(sessionStorage.getItem("transactionFilters") || "{}");

  const [tablePage, setTablePage] = React.useState(
    parseInt(paramsSearch.get("page") ?? savedFilters.page ?? "1", 10)
  );
  const [tableLimit, setTableLimit] = React.useState(
    parseInt(paramsSearch.get("limit") ?? savedFilters.limit ?? "10", 10)
  );

  const getDate = (paramValue, savedValue) => {
    if (paramValue) return new Date(paramValue);
    if (savedValue) return new Date(savedValue);
    return undefined;
  };

  const [filters, setFilters] = React.useState({
    startDate      : getDate(paramsSearch.get("startDate"), savedFilters.startDate),
    endDate        : getDate(paramsSearch.get("endDate"), savedFilters.endDate),
    statusStartDate: getDate(paramsSearch.get("statusStartDate"), savedFilters.statusStartDate),
    statusEndDate  : getDate(paramsSearch.get("statusEndDate"), savedFilters.statusEndDate),
    query          : paramsSearch.get("query") ?? savedFilters.query ?? "",
    userIdQuery    : paramsSearch.get("userIdQuery") ?? savedFilters.userIdQuery ?? "",
    paymentMethod  : paramsSearch.get("paymentMethod") ?? savedFilters.paymentMethod,
    property       : paramsSearch.get("property") ?? savedFilters.property,
    status         : paramsSearch.get("status") ?? savedFilters.status,
    statusDateSort : paramsSearch.get("statusDateSort") ?? savedFilters.statusDateSort ?? "DESC",
  });

  const [query, setQuery] = React.useState(filters.query);
  const [userIdQuery, setUserIdQuery] = React.useState(filters.userIdQuery);
  const paymentMethod = [{ value: "CARD", name: "クレジットカード" }, { value: "TRANSFER", name: "振込" }];
  const columns = useHeader({
    translate,
    s3host,
    roles,
    type    : params.type,
    checkPermission,
    onAction: async (key, record) => {
      switch (key) {
        case "transfer": {
          Modal.confirm({
            title  : translate("payment.list.transfer.modal.title", "決済を振込確認中ステータスへ変更しますか？"),
            icon   : <ExclamationCircleOutlined />,
            content: translate(
              "payment.list.transfer.modal.content",
              "ユーザーを代理して決済を振込確認中ステータスへ変更しますか?"
            ),
            okText    : translate("payment.list.transfer.modal.oktext", "確認"),
            cancelText: translate("system.list.modal.canceltext", "Cancel"),
            onOk      : async () => {
              try {
                await transactionApi.transfer(record._id);
                myTableRef.current.reload();
              } catch (err) {
                message.error(err.message || "Could not get dates");
              }
            },
          });
          break;
        }
        case "pay": {
          Modal.confirm({
            title  : translate("system.list.model.title", "Confirm"),
            icon   : <ExclamationCircleOutlined />,
            content: translate(
              "transaction.list.model.content",
              "Would you like to verify this transaction?"
            ),
            okText    : translate("transaction.list.model.oktext", "Pay"),
            cancelText: translate("system.list.modal.canceltext", "Cancel"),
            onOk      : async () => {
              try {
                await transactionApi.pay({ paymentId: record._id });
                myTableRef.current.reload();
              } catch (err) {
                message.error(err.message || "Could not get dates");
              }
            },
          });
          break;
        }
        case "cancel": {
          Modal.confirm({
            title  : translate("system.list.model.title", "Confirm"),
            icon   : <ExclamationCircleOutlined />,
            content: translate(
              "transaction.list.model.cancel.content",
              "Would you like to cancel this transaction?"
            ),
            okText    : translate("transaction.list.model.cancel.oktext", "キャンセル実行"),
            cancelText: translate("system.list.modal.canceltext", "戻る"),
            onOk      : async () => {
              try {
                await transactionApi.cancel({ paymentId: record._id });
                myTableRef.current.reload();
              } catch (err) {
                message.error(err.message || "Could not get dates");
              }
            },
          });
          break;
        }

        default:
      }
    },
  });

  React.useEffect(() => {
    console.log("[txn] debounce effect", { didInit: didInit.current, query, userIdQuery, fQuery: filters.query, fUserIdQuery: filters.userIdQuery });
    if (!didInit.current) return;
    if (filters.query === query && filters.userIdQuery === userIdQuery) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      console.log("[txn] debounce timeout fired, calling setFilters");
      setFilters((state) => ({ ...state, query, userIdQuery }));
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, userIdQuery]);

  React.useEffect(() => {
    console.log("[txn] save effect", { didInit: didInit.current, filters });
    if (!didInit.current) return;
    const newParams = new URLSearchParams();

    if (filters.query) newParams.set("query", filters.query);
    if (filters.userIdQuery) newParams.set("userIdQuery", filters.userIdQuery);
    if (filters.startDate)
      newParams.set("startDate", filters.startDate.toISOString());
    if (filters.endDate)
      newParams.set("endDate", filters.endDate.toISOString());
    if (filters.statusStartDate)
      newParams.set("statusStartDate", filters.statusStartDate.toISOString());
    if (filters.statusEndDate)
      newParams.set("statusEndDate", filters.statusEndDate.toISOString());
    if (filters.paymentMethod) newParams.set("paymentMethod", filters.paymentMethod);
    if (filters.property) newParams.set("property", filters.property);
    if (filters.status) newParams.set("status", filters.status);
    if (filters.statusDateSort) newParams.set("statusDateSort", filters.statusDateSort);
    newParams.set("page", tablePage);
    newParams.set("limit", tableLimit);

    sessionStorage.setItem("transactionFilters", JSON.stringify({ ...filters, page: tablePage, limit: tableLimit }));
    history.replace({ search: newParams.toString() });
  }, [filters, tablePage, tableLimit]);

  // Controlling the page disables MyTable's built-in reset, so reset to page 1
  // ourselves whenever the filters change.
  React.useEffect(() => {
    if (!didInit.current) return;
    setTablePage(1);
  }, [filters]);

  React.useEffect(() => {
    if (!didInit.current) return;
    setQuery("");
    setUserIdQuery("");
    setFilters({
      startDate      : undefined,
      endDate        : undefined,
      statusStartDate: undefined,
      statusEndDate  : undefined,
      query          : "",
      userIdQuery    : "",
      paymentMethod  : undefined,
      property       : undefined,
      status         : undefined,
      statusDateSort : "DESC",
    });
  }, [params.type]);

  React.useEffect(() => {
    console.log("[txn] didInit setter running");
    didInit.current = true;
  }, []);

  const dateFilter = (e) => {
    setFilters({
      ...filters,
      startDate: e && moment(e[0]).startOf("days").toDate(),
      endDate  : e && moment(e[1]).endOf("days").toDate(),
    });
  };
  const statusDateFilter = (e) => {
    setFilters({
      ...filters,
      statusStartDate: e && moment(e[0]).startOf("days").toDate(),
      statusEndDate  : e && moment(e[1]).endOf("days").toDate(),
    });
  };
  const onClear = () => {
    setQuery("");
    setUserIdQuery("");
    setFilters({
      startDate     : undefined,
      property      : undefined,
      endDate       : undefined,
      query         : "",
      userIdQuery   : "",
      statusDateSort: "DESC"
    });
  };

  return (
    <PageContainer>
      <PageHeader
        title={translate("transaction.pageheader.title", "Transaction")}
      />
      <PageContent>
        <Filter
          extra={[
            <DatePicker.RangePicker
              value={[
                filters.statusStartDate && moment(filters.statusStartDate),
                filters.statusEndDate && moment(filters.statusEndDate),
              ]}
              onChange={statusDateFilter}
              placeholder={[translate("payment.history.statusdate.start", "決済日から"), translate("payment.history.statusdate.end", "決済日まで")]}
            />,
            <Select
              value={filters.statusDateSort || "DESC"}
              style={{ width: "100px" }}
              onChange={(e) => setFilters({ ...filters, statusDateSort: e })}
              placeholder={"表示順"}
            >
              <Select.Option key={1} value={"ASC"}>
                昇順
              </Select.Option>
              <Select.Option key={2} value={"DESC"}>
                降順
              </Select.Option>
            </Select>,
            <DatePicker.RangePicker
              value={[
                filters.startDate && moment(filters.startDate),
                filters.endDate && moment(filters.endDate),
              ]}
              onChange={dateFilter}
              placeholder={[translate("payment.history.start.start", "利用開始日から"), translate("payment.history.start.end", "利用開始日まで")]}
            />,
            <Button onClick={onClear}>
              {translate("system.filter.btn.clear", "Clear")}
            </Button>,
          ]}
        >
          <Input
            className="grid-item"
            prefix={<SearchOutlined />}
            value={userIdQuery}
            placeholder={translate(
              "system.input.search.userId",
              "ユーザー名、ユーザーID"
            )}
            onChange={(e) => setUserIdQuery(e.target.value)}
            allowClear
          />
          <Input
            className="grid-item"
            prefix={<SearchOutlined />}
            value={query}
            placeholder={translate(
              "system.input.search.orderid",
              "Search by order id..."
            )}
            onChange={(e) => setQuery(e.target.value)}
            allowClear
          />
          <Select
            className="grid-item"
            value={filters.status}
            style={{ width: "130px" }}
            onChange={(e) => setFilters({ ...filters, status: e })}
            placeholder={translate("property.list.select.status", "Status")}
            allowClear
          >
            {paymentStatus.map((item, index) => (
              <Select.Option key={index} value={item}>
                {translate(item, item)}
              </Select.Option>
            ))}
          </Select>
          <Select
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            style={{ width: 300 }}
            value={filters.property}
            onChange={(e) => setFilters({ ...filters, property: e })}
            placeholder={translate("income.list.select.property", "Property ")}
            allowClear
            options={properties.map((item) => {
              return {
                value: item._id,
                label: item.name,
              };
            })}
          ></Select>
          <Select
            className="grid-item"
            value={filters.paymentMethod}
            style={{ width: "130px" }}
            onChange={(e) => setFilters({ ...filters, paymentMethod: e })}
            placeholder={translate("payment.method.select", "Select")}
            allowClear
          >
            {paymentMethod.map((item, index) => (
              <Select.Option key={index} value={item.value}>
                {item.name}
              </Select.Option>
            ))}
          </Select>
        </Filter>
        <Table
          ref={myTableRef}
          columns={columns}
          filters={filters}
          loadData={transactionApi.list}
          rowKey={(record) => record?.rowKey}
          page={tablePage}
          onPageChange={setTablePage}
          limit={tableLimit}
          onLimitChange={setTableLimit}
        />
      </PageContent>
    </PageContainer>
  );
};

const useHeader = ({ onAction, translate }) => {
  return [
    {
      title    : "№",
      dataIndex: "rowKey",
      width    : "20px",
    },

    {
      title : translate("system.useheader.title.owner", "Owner"),
      render: (row) => {
        return <div style={{ minWidth: 100 }} >{renderUser(row.owner)}</div>;
      },
    },
    {
      title : translate("system.useheader.title.tenant.user", "ユーザー"),
      render: (row) => {
        return renderUser(row.tenant);
      },
    },
    {
      title : translate("system.useheader.title.tenant.user", "ユーザーID"),
      render: (row) => {
        return <div>{row.tenant?.userId || "-"}</div>;
      },
    },
    {
      title : translate("system.useheader.list.property", "Property"),
      render: (row) => {
        return (
          <Space>
            <Avatar size={45} shape="square" src={row?.property?.image} />
            <div style={{ minWidth: 250 }}>
              <b>{row?.property?.name}</b> <br />
            </div>
          </Space>
        );
      },
    },
    {
      title : translate("system.useheader.title.orderid", "Order ID"),
      render: (record) => {
        return <Tag>{record?.order?.orderId}</Tag>;
      },
    },
    {
      title : translate("system.useheader.title.amount", "Amount"),
      render: (record) => {
        return `${yen(record.amount)}`;
      },
    },
    {
      title : translate("system.useheader.title.tax", "Tax"),
      render: (record) => {
        return `${yen(record.tax)}`;
      },
    },
    {
      title : translate("system.useheader.title.total", "Total"),
      render: (record) => {
        return `${yen(record.total)}`;
      },
    },
    {
      title : translate("system.useheader.title.method", "Method"),
      render: (record) => {
        if (record?.paymentMethod === "TRANSFER"){
          return "振込";
        }
        if (record?.paymentMethod === "CARD"){
          return "クレジットカード";
        }
        return `${record?.paymentMethod || "-"}`;
      },
    },
    {
      title : translate("system.useheader.title.status", "Status"),
      render: (record) => {
        const renderStatus = (status) => {
          if (status === "PENDING")
            return (
              <Tag color="orange">
                {translate(`system.orderstatus.${status}`, status)}
              </Tag>
            );
          if (status === "TRANSFER")
            return (
              <Tag color="orange">
                {translate(`system.orderstatus.${status}`, status)}
              </Tag>
            );
          if (status === "PAID")
            return (
              <Tag color="green">
                {translate(`system.orderstatus.${status}`, status)}
              </Tag>
            );
          if (status === "CANCELLED")
            return (
              <Tag color="red">
                {translate(`system.orderstatus.${status}`, status)}
              </Tag>
            );
        };
        return renderStatus(record.paymentStatus);
      },
    },
    {
      title : translate("transaction.useheader.title.date", "Date"),
      render: (record) => {
        return <div>{datetimeFormat(record.paymentStatusDate)}</div>;
      },
    },
    {
      title : translate("order.list.useheader.dates", "Dates"),
      render: (record) => {
        return (
          <Tag>{`${dateFormat(record.startDate)}-${dateFormat(
            record.endDate
          )}`}</Tag>
        );
      },
    },
    {
      title : translate("transaction.list.gmo", "GMO ID"),
      render: (record) => {
        return <div>{record?.gmoAccessID || "-"}</div>;
      },
    },
    // {
    //   title : translate("system.useheader.title.action", "Action"),
    //   width : 100,
    //   render: (record) => {
    //     if (record.paymentStatus === "TRANSFER") {
    //       return (
    //         <Flex gap={2}>
    //           <Button onClick={() => onAction("pay", record)}>
    //             {translate("transaction.list.action.verify", "Verify")}
    //           </Button>
    //           <Button className='button' type="danger" onClick={() => onAction("cancel", record)}>
    //             {translate("transaction.list.action.cancel", "キャンセル")}
    //           </Button>
    //         </Flex>
    //       );
    //     }
    //     return "-";
    //   },
    // },
    {
      title : translate("system.useheader.title.action", "Action"),
      width : 100,
      render: (record) => {
        let actions = {};
        if (record.paymentStatus === "TRANSFER"){
          actions = {
            pay   : translate("transaction.list.action.verify", "Verify"),
            cancel: translate("transaction.list.action.cancel", "キャンセル")
          };
        }
        if (record.paymentStatus === "PENDING"){
          actions = {
            transfer: translate("transaction.list.action.transfer", "振込確認中"),
          };
        }
        if (record.paymentStatus === "TRANSFER" || record.paymentStatus === "PENDING"){
          return (
            <RowAction
              actions={actions}
              onClick={(key) => onAction(key, record)}
            />
          );
        }
        return "-";
      },
    },
  ];
};

const Table = styled(MyTable)`
  .ant-table-cell {
    padding: 8px;
    line-height: 15px;
  }
`;
